import { ChatMessageFragmentType } from "@/models/ai-chat/ChatMessageFragment";
import { ChatMessageStream, ChatMessageStreamEventType } from "@/models/ai-chat/ChatMessageStream";
import { useEffect, useRef, useState } from "react";
import ChatAvatar from "../ChatAvatar";
import CircleHeartBeatAnimation from "../CircleHeartBeatAnimation";
import TypingAnimation from "../TypingAnimation";
import ChatMessageContainer, { ChatMessageProps } from "./ChatMessageContainer";
import { tryToParseTextPropertyFromIncompleteJson } from "./utilsStreamedMessage";

type Props = ChatMessageProps & {
  streamedMessage: ChatMessageStream;
  handleStreamedMessageFinish: () => void;
  onStreamStart: () => void;
};

export const StreamedChatMessage = (props: Props) => {
  // Until the message is completed, this will be an invalid JSON.
  const rawMessage = useRef<string>("");

  const [messageContent, setMessageContent] = useState<string>();
  const [isStreamingCompleted, setIsStreamingCompleted] = useState<boolean>(false);

  useEffect(() => {
    setMessageContent(props.streamedMessage.content || "");
    setIsStreamingCompleted(false);
    rawMessage.current = "";

    const handleReceiveChunk = (chunk: string) => {
      rawMessage.current += chunk;

      // We should only display Strings while they are being streamed. Every other type must wait until its completed.
      const shouldDisplayWhileStreaming =
        tryToParseTextPropertyFromIncompleteJson("type", rawMessage.current, false) === ChatMessageFragmentType.STRING;

      const isJSON = rawMessage.current.length != 0 && rawMessage.current.trim()[0] == "{";

      props.onStreamStart();
      if (shouldDisplayWhileStreaming) {
        setMessageContent(tryToParseTextPropertyFromIncompleteJson("content", rawMessage.current, true) || "");
      } else if (!isJSON) {
        setMessageContent(rawMessage.current);
      }
    };

    const handleStreamCompletition = () => {
      props.handleStreamedMessageFinish();
      setIsStreamingCompleted(true);
    };

    props.streamedMessage.addEventListener(ChatMessageStreamEventType.CONTENT_RECEIVED, handleReceiveChunk);
    props.streamedMessage.addEventListener(ChatMessageStreamEventType.COMPLETED, handleStreamCompletition);

    return () => {
      props.streamedMessage.removeEventListener(ChatMessageStreamEventType.CONTENT_RECEIVED, handleReceiveChunk);
      props.streamedMessage.removeEventListener(ChatMessageStreamEventType.COMPLETED, handleStreamCompletition);
    };
  }, [props.streamedMessage]);

  const onTypingFinish = () => {
    if (props.streamedMessage.completed) {
      props.handleStreamedMessageFinish();
    }
  };

  return (
    <>
      <ChatAvatar
        userAvatarStyle={{
          justifyContent: "end",
          paddingRight: "0.875rem",
        }}
        isBotMessage={!props.messageSender}
        user={props.lazoUser.user}
      />
      {messageContent && (
        <ChatMessageContainer {...props}>
          <TypingAnimation msPerCharacter={10} text={messageContent} onTypingFinish={onTypingFinish} />
          <>{!isStreamingCompleted && <CircleHeartBeatAnimation />}</>
        </ChatMessageContainer>
      )}
    </>
  );
};
